import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import { EmptyState, LoadingState, MetricCard, PageHeader } from './AdminUI';

const Dashboard = () => {
    const { apiCall } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState({
        blogPosts: 0,
        teamMembers: 0,
        services: 0,
        contacts: 0,
        caseStudies: 0
    });
    const [loading, setLoading] = useState(true);
    const [recentContacts, setRecentContacts] = useState([]);

    useEffect(() => {
        fetchStats();
        fetchRecentContacts();
    }, []);

    const fetchStats = async () => {
        try {
            const [blogRes, teamRes, serviceRes, contactRes, caseStudyRes] = await Promise.all([
                apiCall('/blog'),
                apiCall('/team'),
                apiCall('/services'),
                apiCall('/contact'),
                apiCall('/case-studies')
            ]);

            const blogData = await blogRes.json();
            const teamData = await teamRes.json();
            const serviceData = await serviceRes.json();
            const contactData = await contactRes.json();
            const caseStudyData = await caseStudyRes.json();

            setStats({
                blogPosts: blogData.blogs?.length || blogData.length || 0,
                teamMembers: teamData.length || 0,
                services: serviceData.length || 0,
                contacts: contactData.total || contactData.contacts?.length || 0,
                caseStudies: caseStudyData.caseStudies?.length || caseStudyData.length || 0
            });
        } catch (error) {
            console.error('Error fetching stats:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchRecentContacts = async () => {
        try {
            const response = await apiCall('/contact?limit=5');
            const data = await response.json();
            setRecentContacts(data.contacts || []);
        } catch (error) {
            console.error('Error fetching contacts:', error);
        }
    };

    const statsConfig = [
        { label: 'Blog Posts', value: stats.blogPosts, icon: 'fa fa-pencil-square-o', color: '#62B6E8' },
        { label: 'Team Members', value: stats.teamMembers, icon: 'fa fa-users', color: '#62B6E8' },
        { label: 'Services', value: stats.services, icon: 'fa fa-cogs', color: '#62B6E8' },
        { label: 'Contacts', value: stats.contacts, icon: 'fa fa-envelope', color: '#62B6E8' },
        { label: 'Case Studies', value: stats.caseStudies, icon: 'fa fa-folder-open', color: '#62B6E8' }
    ];

    if (loading) {
        return (
            <AdminLayout>
                <LoadingState label="Loading dashboard..." />
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="admin-page">
            <PageHeader
                title="Dashboard"
                subtitle="Welcome to the StatElites Admin Panel"
            />

            {/* Stats Grid */}
            <div className="analytics-metrics-grid">
                {statsConfig.map((stat, index) => (
                    <MetricCard
                        key={index}
                        label={stat.label}
                        value={stat.value}
                        icon={stat.icon}
                        accent={stat.color}
                        detail="Current total"
                    />
                ))}
            </div>

            {/* Recent Contacts Table */}
            <div className="card">
                <div className="card-header">
                    <h2 className="card-title">Recent Contact Submissions</h2>
                </div>

                <div className="table-responsive">
                    {recentContacts.length === 0 ? (
                        <EmptyState
                            icon="fa fa-envelope-o"
                            title="No Contact Submissions"
                            text="When people contact you through the website, their messages will appear here."
                        />
                    ) : (
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Subject</th>
                                    <th>Service</th>
                                    <th>Status</th>
                                    <th>Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {recentContacts.map((contact) => (
                                    <tr key={contact.id}>
                                        <td>{contact.name}</td>
                                        <td>{contact.email}</td>
                                        <td>{contact.subject}</td>
                                        <td>{contact.requestedService || contact.service || '-'}</td>
                                        <td>
                                            <span className={`status-badge status-${contact.status || 'new'}`}>
                                                {contact.status || 'new'}
                                            </span>
                                        </td>
                                        <td>
                                            {new Date(contact.createdAt).toLocaleDateString()}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Quick Actions */}
            <div style={{ marginTop: '32px' }}>
                <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#222222', marginBottom: '16px' }}>
                    Quick Actions
                </h2>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <button
                        onClick={() => navigate('/admin/blog')}
                        style={{
                            padding: '12px 20px',
                            background: '#62B6E8',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '0',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                            fontFamily: '"Poppins", sans-serif',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.background = '#2F7FAE';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.background = '#62B6E8';
                        }}
                    >
                        <i className="fa fa-plus"></i>
                        New Blog Post
                    </button>

                    <button
                        onClick={() => navigate('/admin/team')}
                        style={{
                            padding: '12px 20px',
                            background: '#62B6E8',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '0',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                            fontFamily: '"Poppins", sans-serif',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.background = '#2F7FAE';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.background = '#62B6E8';
                        }}
                    >
                        <i className="fa fa-user-plus"></i>
                        Add Team Member
                    </button>

                    <button
                        onClick={() => navigate('/admin/contacts')}
                        style={{
                            padding: '12px 20px',
                            background: '#62B6E8',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '0',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                            fontFamily: '"Poppins", sans-serif',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.background = '#2F7FAE';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.background = '#62B6E8';
                        }}
                    >
                        <i className="fa fa-envelope"></i>
                        View Contacts
                    </button>
                </div>
            </div>
            </div>
        </AdminLayout>
    );
};

export default Dashboard;
